/**
 * TanStack Query hook for loading wallets.
 * Admin/stellar_admin: tRPC wallet.list (fetches all pages).
 * Partner: tRPC wallet.listForUser(userId).
 * Returns snake_case wallets from tRPC.
 */

import { useQuery } from "@tanstack/react-query";
import { trpc } from "../trpc";

export function useWalletsQuery(
	userId: string | undefined,
	userRole: string | undefined,
	enabled: boolean,
) {
	const utils = trpc.useUtils();

	return useQuery({
		queryKey: ["wallets", userId, userRole],
		queryFn: async () => {
			if (!userId) return [];
			if (userRole === "admin" || userRole === "stellar_admin") {
				// Fetch all pages via tRPC
				const all: import("../types").Wallet[] = [];
				let page = 1;
				while (true) {
					const result = await utils.wallet.list.fetch({
						page,
						limit: 100,
					});
					all.push(...(result.data as import("../types").Wallet[]));
					if (page >= result.pagination.totalPages) break;
					page++;
				}
				return all;
			}
			return utils.wallet.listForUser.fetch({ userId }) as Promise<
				import("../types").Wallet[]
			>;
		},
		enabled: enabled && !!userId,
		staleTime: 60_000, // 1 min - reduce refetches to avoid 429
		refetchOnMount: true,
		refetchOnWindowFocus: false, // avoid 429 when switching tabs
	});
}
